import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './image-matrix.css';

const ImageMatrix = ({ 
  images, 
  initialDisplayed = [], 
  rows = 2, 
  cols = 2,
  maxWidth = '400px',
  autoChange = false,
  changeInterval = 3000,
  maxChanges = -1,
  fadeOutTime = 450,
  fadeInTime = 450,
  initialSelected = [],
  selectable = false
}) => {

  const [selectedIndexes, setSelectedIndexes] = useState( (selectable) ? initialSelected : []);
  const [displayedImages, setDisplayedImages] = useState(initialDisplayed);
  const [animatingIndex, setAnimatingIndex] = useState(null);
  const [animatingState, setAnimatingState] = useState(null);
  const [changeCount, setChangeCount] = useState(0);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--fade-out-time", 
      `${fadeOutTime}ms`
    );
    document.documentElement.style.setProperty(
      "--fade-in-time", 
      `${fadeInTime}ms`
    );
  }, [fadeOutTime, fadeInTime]);  
  
  // Initialize with first set of images
  useEffect(() => {
    updateDisplayedImages();
  }, [images]);

// Improved random image selection
  const getRandomImage = (currentImages) => {
    const availableImages = images.filter(img => !currentImages.includes(img));
    if (availableImages.length === 0) return null;
    
    // More reliable random index calculation
    const randomIndex = Math.floor(Math.random() * availableImages.length);
    return availableImages[randomIndex];
  };
    
  // Auto-change logic
  useEffect(() => {
    if (!autoChange || (changeCount >= maxChanges && maxChanges > 0) || images.length <= (rows * cols)) 
        return;
    const interval = changeInterval + Math.floor(Math.random() * 2000); // 1-3s variation
    const timer = setTimeout(() => {
      //console.log('Auto-change logic triggered');
      const randomCellIndex = Math.floor(Math.random() * displayedImages.length);
      changeRandomImage(randomCellIndex);
      setChangeCount(prev => prev + 1);
    }, interval);

    return () => clearTimeout(timer);
  }, [displayedImages, changeCount, autoChange]);

  const updateDisplayedImages = () => {
    const totalImages = rows * cols;
    // Fisher-Yates shuffle for better randomness
    const shuffled = [...images];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setDisplayedImages(shuffled.slice(0, totalImages));
  };
  
  const changeRandomImage = (indexToChange) => {
    setAnimatingIndex(indexToChange);
    setAnimatingState({index:indexToChange, state:'fading-out'});
    const newImage = getRandomImage(displayedImages);
    if (!newImage) 
        return;
    
    setTimeout(() => {
        const newDisplayedImages = [...displayedImages];
        newDisplayedImages[indexToChange] = newImage;
        setDisplayedImages(newDisplayedImages);

        setAnimatingState({index:indexToChange, state:'fading-in'});

        setTimeout(() => {
            setAnimatingState(null);
            setAnimatingIndex(null);
        }, fadeInTime);

    }, fadeOutTime);
  };  
      
  const toggleImageSelection = (index) => {
    if (!selectable) return;
    setSelectedIndexes(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const gridStyle = {
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    maxWidth: maxWidth
  };

  return (
    <div className="image-matrix" style={gridStyle}>
      {displayedImages.map((image, index) => {
        const isSelected = selectedIndexes.includes(index);

        const imgState = ((animatingState && animatingState.index === index) ? animatingState.state: 'normal');
        
        return (
          <div 
            key={`${image}-${index}`}
            className={`image-container ${selectable ? 'selectable' : ''} ${isSelected ? 'selected' : ''}`}
            onClick={() => toggleImageSelection(index)}
          >
            <img
              src={image}
              alt={`Selection ${index + 1}`}
              className={`matrix-image ${isSelected ? 'selected' : ''} ${imgState}`}
            />
            {selectable && isSelected && (
              <div className="selection-badge">
                ✓
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

ImageMatrix.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialSelected: PropTypes.arrayOf(PropTypes.number),
  rows: PropTypes.number,
  cols: PropTypes.number,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  autoChange: PropTypes.bool,
  changeInterval: PropTypes.number,
  maxChanges: PropTypes.number,
  fadeOutTime: PropTypes.number,
  fadeInTime: PropTypes.number,
  selectable: PropTypes.bool
};

export default ImageMatrix;